import { useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import { atom, useAtom } from 'jotai';
import { useMemo } from 'react';

import { useTRPC } from '#src/trpc.js';
import { AddCommandBarProvider } from '../components/add-command-bar-provider.js';
import { CreateOrgDialog } from './create-org-dialog.js';

import type { CommandBarProviderProps } from '../command-bar-types.js';

const createOrgDialogOpenAtom = atom(false);

const CREATE_ORG_PROVIDER_ID = 'create-org';

function CreateOrgProvider({ onClose }: CommandBarProviderProps) {
	const [, setIsDialogOpen] = useAtom(createOrgDialogOpenAtom);

	return (
		<Command.Item
			keywords={['new', 'create', 'add', 'org', 'organization']}
			onSelect={() => {
				setIsDialogOpen(true);
				onClose();
			}}
		>
			New Organization
		</Command.Item>
	);
}

/**
 * Registers the "New Organization" command in the command bar globally.
 * Only renders for staff users.
 *
 * The dialog is rendered at this level so it persists after the command bar closes.
 */
export function RegisterCreateOrgProvider() {
	const trpc = useTRPC();
	// Don't throw; this provider renders high up so throwing can break pages where user.getCurrent is supposed to return undefined (/verify-email)
	const { data: currentUser, isSuccess } = useQuery(
		trpc.user.getCurrent.queryOptions(undefined, { throwOnError: false }),
	);
	const [isDialogOpen, setIsDialogOpen] = useAtom(createOrgDialogOpenAtom);

	const provider = useMemo(
		() => ({
			id: CREATE_ORG_PROVIDER_ID,
			component: CreateOrgProvider,
			props: {},
			group: 'Actions',
		}),
		[],
	);

	// Gate on a live successful fetch, not just `data`: React Query keeps the last
	// successful `currentUser` after a later refetch 401s (e.g. on /verify-email),
	// which would otherwise leak the staff-only command to a lapsed session.
	if (!isSuccess || !currentUser?.auth.isStaff) {
		return null;
	}

	return (
		<>
			<AddCommandBarProvider provider={provider} />
			<CreateOrgDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
		</>
	);
}
